import React, { useEffect, useMemo, useState } from 'react';
import { TimeControls } from '../TimeControls';
import { rowHeightFor, useAdmin, useDebounced, useDensity } from '../context';
import { PanelHouse, PanelLocation, PanelMode, WeatherView as WeatherData } from '../types';
import { Empty, Loading, ViewHeader, VirtualList } from '../ui';

// ---------------------------------------------------------------------------
// Teleportation: the registered places
// ---------------------------------------------------------------------------

const TeleportViewInner: React.FC<{ locations: PanelLocation[]; initialQuery: string }> = ({ locations, initialQuery }) => {
  const { act } = useAdmin();
  const density = useDensity();
  const [query, setQuery] = useState(initialQuery);
  const dQuery = useDebounced(query, 120);
  const [cursor, setCursor] = useState(-1);

  useEffect(() => {
    setQuery(initialQuery);
    setCursor(-1);
  }, [initialQuery]);

  const shown = useMemo(() => {
    const q = dQuery.trim().toLowerCase();
    return locations.filter((l) => !q || l.name.toLowerCase().indexOf(q) !== -1);
  }, [locations, dQuery]);

  return (
    <div className="admin-panel__view">
      <ViewHeader title="Téléportation" subtitle={`${locations.length} lieu${locations.length > 1 ? 'x' : ''} enregistré${locations.length > 1 ? 's' : ''}`} />
      <div className="admin-panel__toolbar">
        <input
          className="admin-panel__input admin-panel__input--grow"
          type="search"
          placeholder="Rechercher un lieu"
          aria-label="Rechercher un lieu"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(-1);
          }}
        />
      </div>
      <div className="admin-panel__table admin-panel__table--narrow">
        <VirtualList
          className="admin-panel__table-body"
          items={shown}
          rowHeight={rowHeightFor(density, 46)}
          getKey={(l) => l.name}
          cursor={cursor}
          onCursorChange={setCursor}
          onActivate={(l) => act('teleportLoc', { target: l.name }, `Téléportation : ${l.name}`)}
          ariaLabel="Lieux enregistrés"
          idPrefix="ap-loc"
          empty={
            <Empty
              title={locations.length ? 'Aucun lieu ne correspond' : 'Aucun lieu enregistré'}
              text={locations.length ? 'Essayez un autre mot.' : 'Les lieux se configurent côté serveur (adminTeleportLocations).'}
            />
          }
          renderRow={(l) => (
            <div className="admin-panel__lrow">
              <span className="admin-panel__cell admin-panel__cell--name">{l.name}</span>
              <button
                type="button"
                className="admin-panel__button admin-panel__button--sm"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  act('teleportLoc', { target: l.name }, `Téléportation : ${l.name}`);
                }}
              >
                Téléporter
              </button>
            </div>
          )}
        />
      </div>
      <p className="admin-panel__foot-hint">Flèches pour parcourir, Entrée pour se téléporter au lieu choisi.</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Modes: the admin's own modes
// ---------------------------------------------------------------------------

const ModesViewInner: React.FC<{ modes: PanelMode[] }> = ({ modes }) => {
  const { act } = useAdmin();
  const active = modes.filter((m) => m.active).length;
  return (
    <div className="admin-panel__view">
      <ViewHeader title="Modes" subtitle={`${active} mode${active > 1 ? 's' : ''} actif${active > 1 ? 's' : ''} sur ${modes.length}`} />
      {modes.length === 0 ? (
        <Empty title="Aucun mode disponible" />
      ) : (
        <div className="admin-panel__mode-grid">
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              aria-pressed={m.active}
              className={'admin-panel__mode' + (m.active ? ' admin-panel__mode--active' : '')}
              onClick={() => act('toggleMode', { mode: m.id }, m.label)}
            >
              <span className="admin-panel__mode-name">{m.label}</span>
              <span className="admin-panel__mode-state">{m.active ? 'Actif' : 'Inactif'}</span>
            </button>
          ))}
        </div>
      )}
      <p className="admin-panel__foot-hint">Ces modes ne s'appliquent qu'à vous. Pour un autre joueur, ouvrez sa fiche dans Joueurs.</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Weather and time
// ---------------------------------------------------------------------------

const WeatherViewInner: React.FC<{ weather: WeatherData | null }> = ({ weather }) => {
  const { act } = useAdmin();
  const [query, setQuery] = useState('');
  const dQuery = useDebounced(query, 120);

  const groups = useMemo(() => {
    if (!weather) return [];
    const q = dQuery.trim().toLowerCase();
    const map: Record<string, WeatherData['list']> = {};
    const order: string[] = [];
    for (const w of weather.list) {
      if (q && w.editorId.toLowerCase().indexOf(q) === -1 && (w.category || '').toLowerCase().indexOf(q) === -1) continue;
      const cat = w.category || 'Autres';
      if (!map[cat]) {
        map[cat] = [];
        order.push(cat);
      }
      map[cat].push(w);
    }
    return order.map((cat) => ({ cat, items: map[cat] }));
  }, [weather, dQuery]);

  if (!weather) {
    return (
      <div className="admin-panel__view">
        <ViewHeader title="Météo et heure" />
        <Loading text="Chargement de la météo..." shape="cards" />
      </div>
    );
  }

  return (
    <div className="admin-panel__view admin-panel__view--scroll">
      <ViewHeader title="Météo et heure" subtitle="Le ciel et l'horloge du jeu, pour tous les joueurs.">
        <button type="button" className="admin-panel__button" onClick={() => act('weatherList', {}, 'Actualisation')}>
          Actualiser
        </button>
      </ViewHeader>

      {weather.time ? <TimeControls time={weather.time} /> : null}

      <div className="admin-panel__card admin-panel__card--flat">
        <div className="admin-panel__card-row">
          <div>
            <div className="admin-panel__status-label">Météo actuelle</div>
            <div className="admin-panel__weather-now">
              {weather.current ? weather.current.editorId : '-'}
              <span className="admin-panel__tag">{weather.current && weather.current.forced ? 'Forcée' : 'Cycle automatique'}</span>
            </div>
          </div>
          <button
            type="button"
            className="admin-panel__button"
            disabled={!weather.current || !weather.current.forced}
            onClick={() => act('weatherClear', {}, 'Cycle automatique')}
          >
            Rétablir le cycle automatique
          </button>
        </div>
      </div>

      <div className="admin-panel__toolbar">
        <input
          className="admin-panel__input admin-panel__input--grow"
          type="search"
          placeholder="Filtrer les types de météo"
          aria-label="Filtrer les types de météo"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {groups.length === 0 ? (
        <Empty title="Aucune météo ne correspond" />
      ) : (
        groups.map((g) => (
          <section key={g.cat} className="admin-panel__weather-group" aria-label={g.cat}>
            <h3 className="admin-panel__group-title">{g.cat}</h3>
            <div className="admin-panel__chips">
              {g.items.map((w) => {
                const isCurrent = !!weather.current && parseInt(w.formId, 16) === weather.current.formId;
                return (
                  <button
                    key={w.formId}
                    type="button"
                    aria-pressed={isCurrent}
                    className={'admin-panel__chip' + (isCurrent ? ' admin-panel__chip--active' : '')}
                    onClick={() => act('weatherSet', { target: w.formId.replace(/^0x/i, '') }, w.editorId)}
                  >
                    {w.editorId}
                  </button>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Housing
// ---------------------------------------------------------------------------

const HousingViewInner: React.FC<{ houses: PanelHouse[] }> = ({ houses }) => {
  const { send, confirm } = useAdmin();
  const density = useDensity();
  const [query, setQuery] = useState('');
  const dQuery = useDebounced(query, 120);
  const [cursor, setCursor] = useState(-1);

  const shown = useMemo(() => {
    const q = dQuery.trim().toLowerCase();
    return houses.filter((h) => {
      if (!q) return true;
      return [h.displayName, h.ownerName || '', h.holdName || ''].join(' ').toLowerCase().indexOf(q) !== -1;
    });
  }, [houses, dQuery]);

  const evict = (h: PanelHouse) =>
    confirm({
      title: `Expulser les occupants de ${h.displayName} ?`,
      body: `${h.ownerName ? `${h.ownerName} perd ce logement` : 'Le logement est libéré'}${h.memberCount > 0 ? `, ainsi que ses ${h.memberCount} co-occupant${h.memberCount > 1 ? 's' : ''}` : ''}.`,
      confirmLabel: 'Expulser',
      danger: true,
      onConfirm: () => send({ action: 'housingEvict', target: String(h.refrId) }),
    });

  return (
    <div className="admin-panel__view">
      <ViewHeader title="Logements" subtitle={`${houses.length} logement${houses.length > 1 ? 's' : ''} occupé${houses.length > 1 ? 's' : ''}`} />
      <div className="admin-panel__toolbar">
        <input
          className="admin-panel__input admin-panel__input--grow"
          type="search"
          placeholder="Logement, occupant ou hold"
          aria-label="Rechercher un logement"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(-1);
          }}
        />
      </div>
      <div className="admin-panel__table admin-panel__table--narrow">
        <VirtualList
          className="admin-panel__table-body"
          items={shown}
          rowHeight={rowHeightFor(density, 58)}
          getKey={(h) => h.refrId}
          cursor={cursor}
          onCursorChange={setCursor}
          ariaLabel="Logements occupés"
          idPrefix="ap-house"
          empty={<Empty title={houses.length ? 'Aucun logement ne correspond' : 'Aucun logement occupé'} />}
          renderRow={(h) => (
            <div className="admin-panel__lrow">
              <div className="admin-panel__stack">
                <span className="admin-panel__cell admin-panel__cell--name">{h.displayName}</span>
                <span className="admin-panel__cell admin-panel__cell--muted">
                  {h.ownerName || 'Occupant inconnu'}
                  {h.memberCount > 0 ? ` (+${h.memberCount})` : ''} - {h.holdName || 'Sans hold'}
                </span>
              </div>
              <button
                type="button"
                className="admin-panel__button admin-panel__button--danger admin-panel__button--sm"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  evict(h);
                }}
              >
                Expulser
              </button>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export const TeleportView = React.memo(TeleportViewInner);
export const ModesView = React.memo(ModesViewInner);
export const WeatherView = React.memo(WeatherViewInner);
export const HousingView = React.memo(HousingViewInner);
